/**
 * Table — §7.7 (mission 6 WP03 T020; FR-005, FR-027; style guide §9, §11).
 *
 * `<table>` semantics with `<th scope>`; the container scrolls horizontally,
 * never the page, and carries no shadow. Header row 32px navy-100 overline;
 * body rows 44px (36px `dense`), zebra navy-50, hover navy-100, selected
 * (`data-state="selected"`) accent-50 with a 3px Sky-burst inset bar. Numeric
 * cells are mono, 13px, right-aligned. A focusable row (one that opens the
 * drawer) gets the focus ring; see README "the nested-interactive row".
 *
 * States: rows, and loading (`TableSkeletonRows`, five rows). An empty table
 * is replaced by `EmptyState` (§7.14), never an empty grid.
 */
import * as React from 'react'

import { cn } from './cn'
import { Skeleton } from './skeleton'

export const Table = React.forwardRef<HTMLTableElement, React.TableHTMLAttributes<HTMLTableElement>>(
  ({ className, ...props }, ref) => (
    <div className="relative w-full overflow-x-auto rounded-lg border border-navy-200">
      <table ref={ref} className={cn('w-full caption-bottom border-collapse text-body text-navy-950', className)} {...props} />
    </div>
  ),
)
Table.displayName = 'Table'

export const TableHeader = React.forwardRef<HTMLTableSectionElement, React.HTMLAttributes<HTMLTableSectionElement>>(
  ({ className, ...props }, ref) => (
    <thead ref={ref} className={cn('[&_tr]:h-8 [&_tr]:border-b [&_tr]:border-navy-200', className)} {...props} />
  ),
)
TableHeader.displayName = 'TableHeader'

export const TableBody = React.forwardRef<HTMLTableSectionElement, React.HTMLAttributes<HTMLTableSectionElement>>(
  ({ className, ...props }, ref) => <tbody ref={ref} className={cn('[&_tr:last-child]:border-0', className)} {...props} />,
)
TableBody.displayName = 'TableBody'

export interface TableRowProps extends React.HTMLAttributes<HTMLTableRowElement> {
  /** 36px rows (ledger, audit) instead of 44px (findings, runs). */
  dense?: boolean
}

export const TableRow = React.forwardRef<HTMLTableRowElement, TableRowProps>(
  ({ className, dense = false, ...props }, ref) => (
    <tr
      ref={ref}
      className={cn(
        dense ? 'h-9' : 'h-11',
        'border-b border-navy-200 transition-colors duration-[var(--duration-fast)] even:bg-navy-50 hover:bg-navy-100',
        'data-[state=selected]:bg-[var(--color-accent-50)] data-[state=selected]:[box-shadow:inset_3px_0_0_var(--color-accent)]',
        'focus-visible:outline-none focus-visible:[box-shadow:var(--focus-ring)]',
        className,
      )}
      {...props}
    />
  ),
)
TableRow.displayName = 'TableRow'

export interface TableHeadProps extends React.ThHTMLAttributes<HTMLTableCellElement> {
  /** The column's current sort; sets `aria-sort`. The caller puts a `<button>` inside a sortable header. */
  sort?: 'ascending' | 'descending' | 'none'
}

export const TableHead = React.forwardRef<HTMLTableCellElement, TableHeadProps>(
  ({ className, scope = 'col', sort, ...props }, ref) => (
    <th
      ref={ref}
      scope={scope}
      aria-sort={sort}
      className={cn(
        'h-8 bg-navy-100 px-3 text-left align-middle text-overline uppercase text-navy-600 first:pl-4',
        className,
      )}
      {...props}
    />
  ),
)
TableHead.displayName = 'TableHead'

export interface TableCellProps extends React.TdHTMLAttributes<HTMLTableCellElement> {
  /** Counts: mono 13px, right-aligned, tabular (CLAUDE.md §5). IDs stay left-aligned. */
  numeric?: boolean
}

export const TableCell = React.forwardRef<HTMLTableCellElement, TableCellProps>(
  ({ className, numeric = false, ...props }, ref) => (
    <td
      ref={ref}
      className={cn('px-3 align-middle first:pl-4', numeric && 'font-mono text-body-sm text-right tabular-nums', className)}
      {...props}
    />
  ),
)
TableCell.displayName = 'TableCell'

export const TableCaption = React.forwardRef<HTMLTableCaptionElement, React.HTMLAttributes<HTMLTableCaptionElement>>(
  ({ className, ...props }, ref) => (
    <caption ref={ref} className={cn('mt-3 px-4 pb-3 text-left text-caption text-navy-600', className)} {...props} />
  ),
)
TableCaption.displayName = 'TableCaption'

/** The loading state: five skeleton rows (§7.18). */
export function TableSkeletonRows({ columns, rows = 5 }: { columns: number; rows?: number }) {
  return (
    <>
      {Array.from({ length: rows }, (_, r) => (
        <TableRow key={r} aria-hidden>
          {Array.from({ length: columns }, (_, c) => (
            <TableCell key={c}>
              <Skeleton className="h-4 w-full rounded-sm" />
            </TableCell>
          ))}
        </TableRow>
      ))}
    </>
  )
}
